/* eslint-disable no-promise-executor-return */

import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import store from 'store';

import auth from 'assets/js/auth';
import { ConvertXMLRequestError } from 'assets/js/errors';
import { Toss } from 'assets/js/toss';
import * as cmd from 'assets/js/api/commands';

// hydrationRecursionDepthLimit is the limit for depth to hydrate API response
// data.
const hydrationRecursionDepthLimit = 10;

// maxAttemptsWhen502 is the maximum number of attempts to make when doing a
// Get or Post and a 502 is initially returned.
const maxAttemptsWhen502 = 5;

// msgAuthFail is the expected authorization failure message.
const msgAuthFail = 'UNAUTHORIZED: Authentication failed (Attempted: ' +
  'Token is expired)';

// msgNetworkError is a generic Axios network error to retry.
const msgNetworkError = 'Network Error';

// vueRootCtx is the App.vue root context, for use in accession of things like
// router, and so on.
let vueRootCtx = null;

// sleepBetweenAttempts is the duration to wait between attempts due to a 502
// error occurring.
const sleepBetweenAttempts = 5000;

// sleepBetweenAPICheck is the duration to wait to check if the API is done all
// calls.
const sleepBetweenAPICheck = 500;

// maxAttemptsWhenWaitingForAPICallsToFinish is how many attempts to make every
// 0.5s to wait for the API to finish before giving up.
const maxAttemptsWhenWaitingForAPICallsToFinish = 60;

// apiCallsInprogress tracks the number of API calls presently in progress.
let apiCallsInprogress = 0;

export const AuthErrorString = 'AUTHORIZATION_ERROR';
export const AuthErrorTokenExpString = 'Attempted: Token is expired';
export const AuthErrorTokenExpUnknownString =
  'Attempted: Unknown Authentication';
export const Params = cmd.params;
export const urls = cmd.urls;

export const Init = (rootCtx) => { vueRootCtx = rootCtx; };

export const IsAuthFailureError = (err) => {
  if (err.name !== 'StatusCode401') return false;
  if (err.message === msgAuthFail) return true;
  if (_.includes(err.message, AuthErrorTokenExpString)) return true;
  if (_.includes(err.message, AuthErrorTokenExpUnknownString)) return true;
  return false;
};
const authExpiryFailure = () =>
  vueRootCtx.shipNotification('Your session has expired.', 'danger',
    99999999999);
const authStoreFailure = () =>
  vueRootCtx.shipNotification('You did not have permission to access ' +
    'the resource requested or you have logged out of ' +
    'another session.', 'danger', 99999999999);

// hydrateResponse hydrates an API response.
const hydrateResponse = ({
  data, // Original API data
  recursionDepth = 0,
  shared, // API shared data
}) => {
  // If nothing shared then nothing to hydrate.
  if (!shared) return data;

  // Don't recurse forever.
  if (recursionDepth === hydrationRecursionDepthLimit) return data;

  // If not an object or an array, we're done.
  if (!_.isPlainObject(data) && (!_.isArray(data))) {
    return data;
  }

  _.forEach(data, (res, key) => {
    // Loop over each item.
    if (!res || !res.dehydrated || !res.type || !res.id) {
      if (_.isPlainObject(res) || _.isArray(res)) {
        // eslint-disable-next-line no-param-reassign
        data[key] = hydrateResponse({ data: res, shared });
      }
    } else {
      // Must be a dehydrated objected.
      if (!shared[res.type] || !shared[res.type][res.id]) {
        const err = new Error(`Unable to hydrate ${res.type}:${res.id}`);
        err.name = 'HydrationError';
        throw err;
      }
      // Recursively hydrate the value new value..
      // eslint-disable-next-line no-param-reassign
      data[key] = hydrateResponse(
        {
          data: _.cloneDeep(shared[res.type][res.id]),
          recursionDepth: recursionDepth + 1,
          shared,
        },
      );
    }
  });

  return data;
};

// displayCrashWindow displays a crash window on a 500 error.
const displayCrashWindow = (err) => {
  const errorWindow = window.open();
  if (!errorWindow) return;
  errorWindow.document.open();
  errorWindow.document.write(err.response.data.response);
  errorWindow.document.close();
  const runJsString = (str) => {
    const s = errorWindow.document.createElement('script');
    s.type = 'text/javascript';
    const code = str;
    try {
      s.appendChild(errorWindow.document.createTextNode(code));
      errorWindow.document.body.appendChild(s);
    } catch (e) {
      s.text = code;
      errorWindow.document.body.appendChild(s);
    }
  };
  errorWindow.onload = () => {
    runJsString(err.response.data.js_jquery);
    runJsString(err.response.data.js_debugger);
  };
  const s = errorWindow.document.createElement('style');
  s.innerHTML = err.response.data.css;
  errorWindow.document.getElementsByTagName('head')[0].appendChild(s);
};

// Get sends a GET XML request through the browser. If the authorization
// headers are set in the store, they are automatically set below to
// authenticate the logged in user. If the request requires the user to
// be logged in and they are not, the router is redirected to the login
// page.
export const Get = async (url, opts, hdrs, version = 2, injectAuth = true) => {
  if (!_.isString(url)) throw Error(`url must be a string. got ${typeof url}`);
  if (opts && !_.isPlainObject(opts)) {
    throw Error(`opts must be an object. got ${typeof opts}`);
  }
  if (opts && (!opts.params || !_.isPlainObject(opts.params))) {
    throw Error('opts must have params nested');
  }
  if (hdrs && !_.isPlainObject(hdrs)) {
    throw Error(`hdrs must be an object. got ${typeof hdrs}`);
  }

  const versionString = `application/vnd.qreserve-v${version}`;
  let headers = hdrs;
  if (!headers) headers = {};
  if (!headers.Accept) {
    headers.Accept = `application/vnd.api+json, ${versionString}`;
  }
  if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
  }
  if (_.get(store, 'state.auth.jwt') && injectAuth) {
    // The session is expired. Log out.
    if (moment(store.state.auth.expires).unix() < moment().unix()) {
      vueRootCtx.signOut({ alreadyLoggedOut: true });
      authExpiryFailure();
    }

    // The header is still valid, so use it.
    headers.Authorization = auth.GetAuthorizationHeader();
    headers['VND-QReserve-Token'] = auth.GetAuthorizationHeader();
  }

  // Dehydrate responses by default
  headers['VND-QReserve-Dehydrate'] = 1;

  const options = opts || {};
  options.headers = headers;

  let response;
  let responseErr = null;
  apiCallsInprogress += 1;
  try {
    for (let attempts = 0; attempts <= maxAttemptsWhen502; attempts += 1) {
      try {
        response = await axios.get(url, options);
        if (response.data.data && response.data.shared) {
          response.data.data = hydrateResponse(response.data);
        }

        // Record developer statistics if they are present in the return.
        if (response.data && response.data.developer) {
          try {
            store.dispatch('RecordDeveloperStats',
              { url, opts: _.cloneDeep(opts), response: _.cloneDeep(response) },
            );
          } catch (deverr) {
            // This isn't available in all apps so errors can be thrown.
            // We can safely just ignore them.
          }
        }
        responseErr = null;
        break;
      } catch (err) {
        // Pop up the exception in a new window if we have one.
        if (_.get(err, 'response.data.python_exception', false)) {
          displayCrashWindow(err);
        }

        const errParsed = ConvertXMLRequestError(err);
        let authError = false;
        if (err && err.response) {
          if (IsAuthFailureError(errParsed)) { // Unauthorized
            authError = true;
            vueRootCtx.signOut({ alreadyLoggedOut: true });
            authStoreFailure();
            throw Error(AuthErrorString);
          }
        }

        // All non-authentication errors should be tossed to the handler except
        // for 502 errors. In this case, we retry.
        if (err &&
          err.response &&
          errParsed.name !== 'StatusCode502' &&
          errParsed.name !== 'StatusCode503'
        ) {
          if (!authError) {
            Toss('api.Get', { url, data: null, options }, err);
          }

          throw errParsed;
        } else {
          responseErr = errParsed;
        }
      }

      // Sleep between attempts.
      await new Promise((resolve) => setTimeout(resolve, sleepBetweenAttempts));
    }

    if (!_.isNull(responseErr)) throw responseErr;

    return _.get(response, 'data', {});
  } finally {
    apiCallsInprogress -= 1;
  }
};

// Post sends a POST XML request through the browser. If the authorization
// headers are set in the store, they are automatically set below to
// authenticate the logged in user. If the request requires the user to
// be logged in and they are not, the router is redirected to the login
// page.
export const Post = async (url, data, opts, hdrs) => {
  if (!_.isString(url)) throw Error(`url must be a string. got ${typeof url}`);
  if (opts && !_.isPlainObject(opts)) {
    throw Error(`opts must be an object. got ${typeof opts}`);
  }
  if (hdrs && !_.isPlainObject(hdrs)) {
    throw Error(`hdrs must be an object. got ${typeof hdrs}`);
  }

  let headers = hdrs;
  if (!headers) headers = {};
  if (!headers.Accept) headers.Accept = 'application/vnd.api+json';
  if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
  }
  if (_.get(store, 'state.auth.jwt')) {
    // The session is expired. Log out.
    if (moment(store.state.auth.expires).unix() < moment().unix()) {
      vueRootCtx.signOut();
      authExpiryFailure();
      throw Error(AuthErrorString);
    }

    headers.Authorization = auth.GetAuthorizationHeader();
    headers['VND-QReserve-Token'] = auth.GetAuthorizationHeader();
  }

  // Dehydrate responses by default
  headers['VND-QReserve-Dehydrate'] = 1;

  const options = opts || {};
  options.headers = headers;

  let response;
  let responseErr = null;
  apiCallsInprogress += 1;
  try {
    for (let attempts = 0; attempts <= maxAttemptsWhen502; attempts += 1) {
      try {
        if (data instanceof FormData) {
          response = await axios.postForm(url, data, options);
        } else {
          response = await axios.post(url, data, options);
        }
        if (response.data.data && response.data.shared) {
          response.data.data = hydrateResponse(response.data);
        }
        responseErr = null;
        break;
      } catch (err) {
        // JSON serialization or other error?
        if (err && !err.response && err.message !== msgNetworkError) {
          throw err;
        }

        const errParsed = ConvertXMLRequestError(err);
        let authError = false;
        if (err && err.response) {
          // Pop up the exception in a new window if we have one.
          if (_.get(err, 'response.data.python_exception', false)) {
            displayCrashWindow(err);
          }

          if (IsAuthFailureError(errParsed)) { // Unauthorized
            authError = true;
            vueRootCtx.signOut({ alreadyLoggedOut: true });
            authStoreFailure();
            throw Error(AuthErrorString);
          }
        }

        // All non-authentication errors should be tossed to the handler except
        // for 502 errors. In this case, we retry.
        if (err &&
          err.response &&
          errParsed.name !== 'StatusCode502' &&
          errParsed.name !== 'StatusCode503'
        ) {
          if (!authError) {
            Toss('api.Post', { url, data: null, options }, err);
          }
          throw errParsed;
        } else {
          responseErr = errParsed;
        }
      }

      // Sleep between attempts.
      await new Promise((resolve) => setTimeout(resolve, sleepBetweenAttempts));
    }

    if (!_.isNull(responseErr)) throw responseErr;

    return _.get(response, 'data', {});
  } finally {
    apiCallsInprogress -= 1;
  }
};

// GenerateFreshJWT gets a fresh, unique jwt from an existing saved JWT.
export const GenerateFreshJWT = async (expires) => {
  const now = moment();
  const minutes = -Math.round(
    Number(moment.duration(now.diff(expires)).asMinutes()));

  const resp = await Get(urls.LOGIN, { params: { expiry: minutes } });

  if (resp) {
    return { token: resp.data.token, expires: resp.data.expires };
  }
  return null;
};

export const WaitForAllApiCallsToFinish = async () => {
  for (let i = 0; i < maxAttemptsWhenWaitingForAPICallsToFinish; i += 1) {
    if (apiCallsInprogress === 0) return;
    const sleeper = new Promise((resolve) => setTimeout(resolve,
      sleepBetweenAPICheck));
    await sleeper;
  }
};
